import React, { useEffect, useState } from "react";
import { Row, Table } from "antd";
import { uniqueId } from "lodash";
import moment from "moment";
import { IProductsTable, IProductsVersionsTable } from "../../ProductsTable/ProductsTableInterface";
import { IProduct } from "../../../lib/types";
import { useAppDispatch, useAppSelector } from "../../..";
import { fetchHierarchyProductById } from "../../../store/slices/products/products";
import { checkIsLoading, toHttpFormat } from "../../../lib/utils";
import "./BulkProductsTableModal.less";

interface Props {
  selectedProduct: { productId: string; agreementId: number };
}

function BulkProductsTableModal({ selectedProduct }: Props) {
  const dispatch = useAppDispatch();

  const { serviceTypes } = useAppSelector((state) => state.service);

  const { productHierarchy, productHierarchyLoading } = useAppSelector((state) => state.products);

  const { products, serviceStatus } = useAppSelector((state) => state.rtkDictionaries);

  useEffect(() => {
    dispatch(
      fetchHierarchyProductById({
        productId: selectedProduct.productId,
        onDate: toHttpFormat(moment()),
        agreementId: selectedProduct.agreementId,
        extendedInfo: false,
      })
    );
  }, [selectedProduct]);

  const [onExpandFunctionsForKey, setOnExpandFunctionsForKey] = useState<{
    [key: string]: (e) => void;
  }>({});

  const changeExpandFunctionsForKey = (callBack, record) => {
    if (!onExpandFunctionsForKey[record.key]) {
      setOnExpandFunctionsForKey((prev) => ({
        ...prev,
        [record.key]: (e) => {
          callBack(record, e);
        },
      }));
    }
  };

  const [columns, setColumns] = useState<any>([
    {
      title: "Наименование Предложения",
      dataIndex: "extName",
      key: "extName",
    },
    {
      title: "ID Продукта",
      dataIndex: "id",
      key: "id",
      width: "180px",
    },
    {
      title: "Код Предложения",
      dataIndex: "offeringName",
      key: "offeringName",
      width: "300px",
    },
  ]);

  const [serviceData, setServiceData] = useState<IProductsTable[]>([]);
  const [serviceVersionData, setServiceVersionData] = useState<IProductsVersionsTable[]>([]);

  const formatItemForProductsTable = (productsList: IProduct[]): IProductsTable[] => {
    if (!productsList) return [];

    return productsList.map((item, itemIdx) => ({
      ...item,
      key: uniqueId(),
      extName: item.offering?.extName,
      agreementNumber: item.agreement?.agreementNumber,
      offeringId: item.offering?.id,
      offeringName: item.offering?.name,
      productType: (serviceTypes as { id: number; name: string }[]).find(
        ({ id }) => id === item.serviceType?.id
      )?.name,
      productGroup: (products as { id: number; name: string }[]).find(({ id }) => {
        return id === item.serviceType?.productId;
      })?.name,
      status: (serviceStatus as { id: number; name: string }[]).find(
        ({ id: serviceStatusId }) => String(serviceStatusId) === String(item.serviceStatusId)
      )?.name,
      documentButton: true,
      children: [],
      childrens: [...formatItemForProductsTable(item.children)],
      isVersion: false,
    }));
  };

  useEffect(() => {
    if (productHierarchy) {
      setServiceData(formatItemForProductsTable(productHierarchy));
    }
    if (!productHierarchy) {
      setServiceData([]);
    }
  }, [productHierarchy, serviceTypes, serviceStatus]);

  console.log({ productHierarchy });

  const columnsTable = [...columns];
  columnsTable[0] = { ...columnsTable[0], width: undefined };

  const columnsTableChildren = columnsTable.map((el) => ({ ...el, sortOrder: undefined }));

  const innerRowClassName = (record: IProductsTable) => {
    if (Number(record.id) === Number(selectedProduct.productId)) {
      return "row_child row_current-version";
    }
    return "";
  };

  const [isVersionsOpen, setIsVersionsOpen] = useState<{
    id?: number;
    visible: boolean;
    key?: string | number;
  }>({
    visible: false,
  });

  const expandedRowRender = (
    record: IProductsTable | IProductsVersionsTable,
    depth: number,
    depthP: number,
    depthChild
  ) => {
    let listVisibleVersions = record?.childrens;
    if (isVersionsOpen.visible && isVersionsOpen.id === record.id) {
      listVisibleVersions = [...serviceVersionData];
      if (record?.childrens) {
        listVisibleVersions.push(...record?.childrens);
      }
    }

    return (
      <Table
        columns={columnsTableChildren}
        dataSource={listVisibleVersions}
        rowClassName={innerRowClassName}
        size="small"
        pagination={false}
        showHeader={false}
        expandable={{
          indentSize: 0,
          expandedRowRender: (recordRow) => {
            // Проверка есть ли дети в иерархии и сколько их
            const isChildNoMore = recordRow?.childrens && recordRow?.childrens.length === 1;

            // Проверка последний ли ребёнок строка
            const isLastChild =
              recordRow?.id === record?.childrens?.[record?.childrens?.length - 1]?.id;

            // Генерируем для следующего уровня проверку, нужно ли ему отображать пунктирную линию предыдущего уровня и всех до него
            const getDepthChildNext = () => {
              const depthChildNext = { ...depthChild };

              if (isChildNoMore) {
                depthChildNext[depthP + 1] = false;
                depthChildNext[depthP] = isLastChild ? false : depthChild[depthP];
              } else {
                depthChildNext[depthP + 1] = true;
                depthChildNext[depthP] = isLastChild ? false : depthChild[depthP];
              }

              return depthChildNext;
            };

            // Рекурсивно вызываем таблицу для создания иерархии
            return expandedRowRender(recordRow, depth + 4.7, depthP + 1, getDepthChildNext());
          },
          rowExpandable: (recordRow) => {
            // Расширяемая таблица только для строк у которых есть иерархия тоесть кнопки или если просматриваем версии этой строки
            return (
              (!!recordRow?.childrens && recordRow?.childrens?.length > 0) ||
              recordRow.id === isVersionsOpen?.id
            );
          },
          expandIcon: ({ expanded, onExpand, record: recordRow }) => {
            // Записываем в состояние все функции открытия, так как по другому её не достать для вызова вне expandable функции
            changeExpandFunctionsForKey(onExpand, recordRow);

            // Генерируем пустой массив и его дляну = (количество пунктирных линий которые нам надо отрисовать по порядку)
            const arr: string[] = [];
            arr.length = depthP - 1;
            arr.fill("");

            // Условие для строк где есть кнопки + -
            if (!!recordRow?.childrens && recordRow?.childrens?.length > 0) {
              return expanded ? (
                <>
                  <Row
                    wrap={false}
                    style={{
                      marginLeft: `${9 * depth}px`,
                      position: "absolute",
                      top: 0,
                      width: 34,
                      height: "50%",
                      borderLeft: "1px dashed #AFB5C1",
                      borderBottom: !recordRow.isVersion ? "1px dashed #AFB5C1" : "",
                    }}
                  >
                    <button
                      style={{ left: "100%", position: "absolute", bottom: "-9px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExpand(recordRow, e);
                      }}
                      className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded"
                      type="button"
                    />
                  </Row>

                  {arr.map((_, index) => {
                    if (!depthChild[index + 1]) {
                      return null;
                    }
                    return (
                      <Row
                        key={index}
                        wrap={false}
                        style={{
                          marginLeft: `${9 * (index + 1) + (index > 0 ? 33.3 * index : 0)}px`,
                          position: "absolute",
                          top: 0,
                          width: 34,
                          height: "100%",
                          borderLeft: "1px dashed #AFB5C1",
                          zIndex: 2,
                        }}
                      />
                    );
                  })}

                  {record?.childrens &&
                    record?.childrens[record?.childrens?.length - 1].id !== recordRow.id && (
                      <Row
                        wrap={false}
                        style={{
                          marginLeft: `${9 * depth}px`,
                          position: "absolute",
                          top: 0,
                          width: 34,
                          height: "100%",
                          borderLeft: "1px dashed #AFB5C1",
                          zIndex: 1,
                        }}
                      />
                    )}
                  <div
                    style={{
                      width: `${9 * depth + 50}px`,
                    }}
                  />
                  {!record.isVersion && (
                    <div
                      style={{
                        width: `${9 * depth + 43.3}px`,
                        position: "absolute",
                        height: "calc(50% - 8px)",
                        borderRight: "1px dashed #AFB5C1",
                        bottom: 0,
                      }}
                    />
                  )}
                </>
              ) : (
                <>
                  <Row
                    wrap={false}
                    style={{
                      marginLeft: `${9 * depth}px`,
                      position: "absolute",
                      top: 0,
                      width: 34,
                      height: "50%",
                      borderLeft: "1px dashed #AFB5C1",
                      borderBottom: !recordRow.isVersion ? "1px dashed #AFB5C1" : "",
                    }}
                  >
                    <button
                      style={{ left: "100%", position: "absolute", bottom: "-9px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExpand(recordRow, e);
                      }}
                      className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
                      type="button"
                    />
                  </Row>

                  {arr.map((item, index) => {
                    if (!depthChild[index + 1]) {
                      return null;
                    }
                    return (
                      <Row
                        key={index}
                        wrap={false}
                        style={{
                          marginLeft: `${9 * (index + 1) + (index > 0 ? 33.3 * index : 0)}px`,
                          position: "absolute",
                          top: 0,
                          width: 34,
                          height: "100%",
                          borderLeft: "1px dashed #AFB5C1",
                          zIndex: 2,
                        }}
                      />
                    );
                  })}

                  {record?.childrens &&
                    record?.childrens[record?.childrens?.length - 1].id !== recordRow.id && (
                      <Row
                        wrap={false}
                        style={{
                          marginLeft: `${9 * depth}px`,
                          position: "absolute",
                          top: 0,
                          width: 34,
                          height: "100%",
                          borderLeft: "1px dashed #AFB5C1",
                          zIndex: 1,
                        }}
                      />
                    )}
                  <div
                    style={{
                      width: `${9 * depth + 50}px`,
                    }}
                  />
                </>
              );
            }
            // Условия для строк без кнопок + -
            return (
              <>
                {(recordRow.isVersion ? record.childrens?.length !== 0 : true) && (
                  <Row
                    wrap={false}
                    style={{
                      marginLeft: `${9 * depth}px`,
                      position: "absolute",
                      top: 0,
                      width: 34,
                      height: "50%",
                      borderLeft: "1px dashed #AFB5C1",
                      borderBottom: !recordRow.isVersion ? "1px dashed #AFB5C1" : "",
                    }}
                  />
                )}

                {arr.map((_, index) => {
                  if (!depthChild[index + 1]) {
                    return null;
                  }
                  return (
                    <Row
                      key={index}
                      wrap={false}
                      style={{
                        marginLeft: `${9 * (index + 1) + (index > 0 ? 33.3 * index : 0)}px`,
                        position: "absolute",
                        top: 0,
                        width: 34,
                        height: "100%",
                        borderLeft: "1px dashed #AFB5C1",
                        zIndex: 2,
                      }}
                    />
                  );
                })}

                {record?.childrens &&
                  record?.childrens[record?.childrens?.length - 1]?.id !== recordRow.id &&
                  (recordRow.isVersion ? record.childrens.length !== 0 : true) && (
                    <Row
                      wrap={false}
                      style={{
                        marginLeft: `${9 * depth}px`,
                        position: "absolute",
                        top: 0,
                        width: 34,
                        height: "100%",
                        borderLeft: "1px dashed #AFB5C1",
                        zIndex: 1,
                      }}
                    />
                  )}
                {recordRow.isVersion ? (
                  <div
                    style={{
                      width: `${9 * depth + 34}px`,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: `${9 * depth + 34}px`,
                    }}
                  />
                )}
              </>
            );
          },
        }}
      />
    );
  };

  return (
    <Table
      className="bulkProduct-list-table"
      columns={columns}
      dataSource={serviceData}
      size="small"
      pagination={false}
      rowClassName={innerRowClassName}
      scroll={{
        x: "800px",
      }}
      loading={checkIsLoading(productHierarchyLoading)}
      expandable={{
        indentSize: 0,
        expandedRowRender: (record) =>
          expandedRowRender(
            record,
            1,
            1,
            record?.childrens && record?.childrens.length === 1 ? {} : { 1: true }
          ),
        rowExpandable: (recordRow) => {
          // Расширяемая таблица только для строк у которых есть иерархия тоесть кнопки или если просматриваем версии этой строки
          return (
            (!!recordRow?.childrens && recordRow?.childrens?.length > 0) ||
            recordRow.id === isVersionsOpen?.id
          );
        },
        expandIcon: ({ expanded, onExpand, record }) => {
          changeExpandFunctionsForKey(onExpand, record);

          if (!!record?.childrens && record?.childrens?.length > 0) {
            return expanded ? (
              <>
                <Row>
                  <button
                    style={{ marginLeft: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                    className="ant-table-row-expand-icon ant-table-row-expand-icon-expanded"
                    type="button"
                  />
                </Row>
                <div
                  style={{
                    width: `${10}px`,
                    position: "absolute",
                    height: "calc(50% - 8px)",
                    borderRight: "1px dashed #AFB5C1",
                    bottom: 0,
                  }}
                />
              </>
            ) : (
              <Row>
                <button
                  style={{ marginLeft: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpand(record, e);
                  }}
                  className="ant-table-row-expand-icon ant-table-row-expand-icon-collapsed"
                  type="button"
                />
              </Row>
            );
          }
          return null;
        },
      }}
    />
  );
}

export default BulkProductsTableModal;
